// SearchContext.tsx
import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { IPlayerSearchResponse } from '../types/IPlayerSearchResponse';
import { ISimilarPlayerCardDetail } from '../types/IPlayerDetail';
import { ISelectedAttribute, ISimilarPlayerFilterOptions } from '../types/IPlayerSearchFilter';
import { IAttribute, ISection } from '../components/PlayerLanding/PlayerSearch/SelectAttributes';
import { ITag } from '../components/PlayerLanding/PlayerSearch/SelectTags';

interface ISearchContextType {
    isSearching: boolean;
    toggleSearch: () => void;
    searchData?: IPlayerSearchResponse[];
    setSearchData?: ((data: IPlayerSearchResponse[]) => void) | undefined;
    searchText: string; // Add searchText to the context state
    setSearchText: (text: string) => void;
    isAdding?: boolean;
    toggleAdd: () => void;
    isActive: string;
    setIsActiveButton: (activeButton: string) => void;
    toggleZone: () => void;
    selectedZones: { id: number; name: string; }[];
    handleSelectedZoneIdsChange: (zoneIds: { id: number; name: string; }[]) => void;
    handleSelectedPlayers: (players: IPlayerSearchResponse[]) => void;
    selectedPlayers: any[];
    handleSelectedProfiles: (profileIds: number[]) => void;
    selectedProfiles: number[];
    handleSearchResults: (similarPlayerResult: ISimilarPlayerCardDetail) => void;
    similarPlayersResults: ISimilarPlayerCardDetail | undefined;
    originsPage: string;
    handleOriginPage: (page: string) => void;
    isClubsSelected: boolean;
    setIsClubsSelected: (value: boolean) => void;
    filterValues: ISimilarPlayerFilterOptions;
    setFilterValues: (values: ISimilarPlayerFilterOptions) => void;
    isZoneProfileChangeFromCandiates: boolean;
    setIsZoneProfileChangeFromCandiates: (value: boolean) => void;
    isFilterChange: boolean;
    setIsFilterChange: (value: boolean) => void;
    sortOrderName: string;
    setSortOrderName: (sortValue: string) => void;
    sortOrdering: string;
    setSortOrdering: (sortOrdering: string) => void;
    seasonId: number;
    setSeasonId: (seasonId: number) => void;
    attributesDrawerOpen: boolean;
    setAttributesDrawerOpen: (value: boolean) => void;
    tagsDrawerOpen: boolean;
    setTagsDrawerOpen: (value: boolean) => void;
    selectedAttributesState: IAttribute[];
    handleSelectedAttributes: (selectedAttribute: IAttribute[]) => void;
    isSearchByAttributes: boolean;
    setIsSearchByAttributes: (value: boolean) => void;
    isAllPosition: boolean;
    setIsAllPosition: (value: boolean) => void;
    selectedTags: ITag[];
    handleSelectedTags: (selectedTag: ITag[]) => void;

    playerId: number | null;
    setPlayerId: (id: number | null) => void;
    playerName: string;
    setPlayerName: (name: string) => void;
    isVolatility: boolean;
    setVolatility: (value: boolean) => void;
    isTransferMarket: boolean;
    setIsTransferMarket: (value: boolean) => void;
    selectedOption: string;
    setSelectedOption: (value: string) => void;

    selectedAttr: string;
    setSelectedAttr: (value: string) => void;
    
    playerPrimaryPosition: string;
    setPlayerPrimaryPosition: (value: string) => void;

    attrDescription :string;
    setAttributesDescription: (value: string) => void;

    attrInfoLabel :string;
    setAttrInfiLabel: (value: string) => void;

    isInfocard : boolean;
    setIsInfoCard : (value: boolean) => void;

    attrIndex : number;
    setCurrentAttrIndex: React.Dispatch<React.SetStateAction<number>>;

    selectedPositionIds : number[];
    setSelectedPositionIds : React.Dispatch<React.SetStateAction<number[]>>;

    selectedGraph : string;
    setSelectedGraph : React.Dispatch<React.SetStateAction<string>>;

    
}

const PlayerSearchContext = createContext<Partial<ISearchContextType>>({});

export const useSearch = (): ISearchContextType => {
    const context = useContext(PlayerSearchContext);
    if (!context) {
        throw new Error('useSearch must be used within a SearchProvider');
    }
    return context as ISearchContextType;
};

export const SearchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isSearching, setIsSearching] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchData, setSearchData] = useState<IPlayerSearchResponse[]>([]);
    const [isActive, setIsActive] = useState('');
    const [selectedZones, setSelectedZones] = useState<{ id: number; name: string; }[]>([]);
    const [selectedPlayers, setSelectedPlayers] = useState<IPlayerSearchResponse[]>([]);
    const [selectedProfiles, setSelectedProfiles] = useState<number[]>([]);
    const [similarPlayersResults, setSimilarPlayersResults] = useState<ISimilarPlayerCardDetail>();
    const [originsPage, setOriginsPage] = useState('');
    const [isClubsSelected, setIsClubsSelected] = useState(false);
    const [isZoneProfileChangeFromCandiates, setIsZoneProfileChangeFromCandiates] = useState(false);
    const [selectedPositionIds, setSelectedPositionIds] = useState<number[]>([]);
    const [selectedGraph, setSelectedGraph] = useState<string>('Football');
    
    const initialFilterValues: ISimilarPlayerFilterOptions = {
        foot: [],
        minAge: 16,
        maxAge: 50,
        minTrueValue: 0,
        maxTrueValue: 0,
        minTransferValue: 10000,
        maxTransferValue: 180000000,
        minContractUntil: 0,
        maxContractUntil: 35,
        regionValues: [],
        competitionValues: [],
        minAppearances: 0,
        startsOnly: true,
        positions: 'allpositions',
        selectedAttributes: [],
        selectedTags: []
    };
    const [filterValues, setFilterValues] = useState<ISimilarPlayerFilterOptions>(initialFilterValues);
    const [isFilterChange, setIsFilterChange] = useState(false);
    const [sortOrderName, setSortOrderName] = useState('');
    const [sortOrdering, setSortOrdering] = useState('');
    const [seasonId, setSeasonId] = useState(0);
    const [attributesDrawerOpen, setAttributesDrawerOpen] = useState<boolean>(false);
    const [tagsDrawerOpen, setTagsDrawerOpen] = useState<boolean>(false);
    const [selectedAttributesState, setSelectedAttributesState] = useState<IAttribute[]>([]);
    const [attributesState, setAttributesState] = useState<IAttribute[]>([]);
    const [isSearchByAttributes, setIsSearchByAttributes] = useState<boolean>(false);
    const [isAllPosition, setIsAllPosition] = useState<boolean>(true);
    const [selectedTags, setSelectedTags] = useState<ITag[]>([]);

    const [playerId, setPlayerId] = useState<number | null>(null);
    const [playerName, setPlayerName] = useState<string>('');
    const [isVolatility, setVolatility] = useState<boolean>(false);
    const [isTransferMarket, setIsTransferMarket] = useState<boolean>(true);
    const [selectedOption, setSelectedOption] = useState<string>('Matches');
    const [playerPrimaryPosition , setPlayerPrimaryPosition] = useState<string>('')

    const [selectedAttr, setSelectedAttr] = useState<string>('');
    const [attrDescription , setAttributesDescription] = useState<string>('Each dot represents a single shot attempt. Dot colour represents effectiveness:');
    const [attrInfoLabel , setAttrInfiLabel] = useState<string>('')
    const [isInfocard, setIsInfoCard] = useState<boolean>(false)
    const [attrIndex , setCurrentAttrIndex] = useState<number>(0)


    useEffect(()=>{
        if (
          ['lb', 'lwb', 'rwb', 'rw', 'lcb3', 'lcb', 'cb', 'rcb', 'rcb3'].includes(playerPrimaryPosition)
        ) {
          setSelectedAttr('Tackling');
          setAttributesDescription('Each dot represents a tackling attempt.')
        } else if (
          ['ldmf', 'dmf', 'rdmf', 'lcmf', 'rcmf', 'lw', 'rb',].includes(playerPrimaryPosition)
        ) {
          setSelectedAttr('Creativity');
          setAttributesDescription('The heat is made from a player’s creative actions. ')
        } else if (
          ['lamf', 'amf', 'ramf', 'lwf', 'rwf', 'st'].includes(playerPrimaryPosition)
        ) {
          setSelectedAttr('Finishing - Shots');
        }
        else{
            setSelectedAttr('Finishing - Shots');
        }
    },[playerPrimaryPosition])

    useEffect(()=>{
        if(selectedAttr === 'Finishing - Shots' || selectedAttr === 'Finishing - Aerial' || selectedAttr ===  'Crossing'){ setAttrInfiLabel('Event markers')}
        if(selectedAttr === 'Creativity' || selectedAttr === 'Passing accuracy' || selectedAttr === 'Dribbling' || selectedAttr === 'Attack positioning'){ setAttrInfiLabel('Heat signature')}
        if(selectedAttr === 'Tackling' || selectedAttr === 'Defensive headers' || selectedAttr ===  'Ball recovery'){ setAttrInfiLabel('Defensive hull')}
    },[selectedAttr])

    
    const toggleSearch = () => {
        setIsSearching(prev => !prev);
        setSearchData([]);
        setSelectedProfiles([]);
        setSelectedPlayers([]);
        setSelectedZones([]);
        setFilterValues(initialFilterValues);
        setIsActive('Search');
        setIsFilterChange(false);
    };
    const toggleAdd = () => {
        setIsAdding(prev => !prev);
        setSearchData([]);
        setSelectedProfiles([]);
        setSelectedPlayers([]);
        setSelectedZones([]);
        setFilterValues(initialFilterValues);
        setIsActive('Similar Players');
        setIsFilterChange(false);
    };

    const toggleZone = () => {
        setSearchData([]);
        setSelectedZones([]);
        setSelectedProfiles([]);
        setSelectedPlayers([]);
        setFilterValues(initialFilterValues);
        setIsActive('Zones');
        setIsFilterChange(false);
    }
    const setIsActiveButton = (activeValue: string) => {
        setIsActive(activeValue);
    }

    const handleSelectedZoneIdsChange = (zoneIds: { id: number; name: string; }[]) => { // Method to update selected zone IDs
        setSelectedZones(zoneIds);
    };

    const handleSelectedPlayers = (players: IPlayerSearchResponse[]) => {
        setSelectedProfiles([]);
        setSelectedPlayers(players);
    }

    const handleSelectedProfiles = (profileIds: number[]) => {
        // setSelectedPlayers([]);
        setSelectedProfiles(profileIds);
    }

    const handleSearchResults = (similarPlayerResult: ISimilarPlayerCardDetail) => {
        setSimilarPlayersResults(similarPlayerResult);
    }

    const handleOriginPage = (page: string) => {
        setOriginsPage(page);
    }

    const handleSelectedAttributes = (selectedAttribute: IAttribute[]) => {
        // setSelectedPlayers([]);
        setSelectedAttributesState(selectedAttribute);
    }

    const handleSelectedTags = (selectedTags: ITag[]) => {
        // setSelectedPlayers([]);
        setSelectedTags(selectedTags);
    }



    return (
        <PlayerSearchContext.Provider value={{
            isSearching,
            toggleSearch,
            searchData,
            setSearchData,
            searchText,
            setSearchText,
            isAdding,
            toggleAdd,
            isActive,
            setIsActiveButton,
            toggleZone,
            selectedZones,
            handleSelectedZoneIdsChange,
            handleSelectedPlayers,
            selectedPlayers,
            handleSelectedProfiles,
            selectedProfiles,
            handleSearchResults,
            similarPlayersResults,
            handleOriginPage,
            originsPage,
            isClubsSelected,
            setIsClubsSelected,
            filterValues,
            setFilterValues,
            isZoneProfileChangeFromCandiates,
            setIsZoneProfileChangeFromCandiates,
            isFilterChange,
            setIsFilterChange,
            sortOrderName,
            setSortOrderName,
            sortOrdering,
            setSortOrdering,
            seasonId,
            setSeasonId,
            attributesDrawerOpen,
            setAttributesDrawerOpen,
            tagsDrawerOpen,
            setTagsDrawerOpen,
            selectedAttributesState,
            handleSelectedAttributes,
            isSearchByAttributes,
            setIsSearchByAttributes,
            isAllPosition,
            setIsAllPosition,
            selectedTags,
            handleSelectedTags,

            playerId,
            setPlayerId,
            playerName,
            setPlayerName,
            isVolatility,
            setVolatility,
            isTransferMarket,
            setIsTransferMarket,
            selectedOption,
            setSelectedOption,
            selectedAttr,
            setSelectedAttr,

            playerPrimaryPosition,
            setPlayerPrimaryPosition,

            attrDescription , 
            setAttributesDescription,
            attrInfoLabel , 
            setAttrInfiLabel,

            isInfocard, 
            setIsInfoCard,

            attrIndex,
            setCurrentAttrIndex,
            selectedPositionIds, 
            setSelectedPositionIds,
            selectedGraph, setSelectedGraph
            
        }}>
            {children}
        </PlayerSearchContext.Provider>
    );
};
